// Project 8: 발행 서비스
import axios from 'axios';
import type {
  PublishRequest,
  PublishResponse,
  PublishStatusResponse,
  PublishHistoryResponse,
  PublishTarget,
  TokenSaveRequest,
  TokenSaveResponse
} from '@/types/publish';

const apiClient = axios.create({
  baseURL: '/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

export class PublishService {
  /**
   * 발행 대상 플랫폼 목록 조회
   */
  async getTargets(): Promise<{ success: boolean; data?: { targets: PublishTarget[] }; error?: any }> {
    try {
      const response = await apiClient.get<{ success: boolean; data?: { targets: PublishTarget[] }; error?: any }>(
        '/v2/publish/targets'
      );
      return response.data;
    } catch (error: any) {
      console.error('Publish targets error:', error);
      return {
        success: false,
        error: error.response?.data?.error || { code: 'FETCH_FAILED', message: '플랫폼 목록 조회에 실패했습니다.' }
      };
    }
  }

  /**
   * 콘텐츠 발행
   */
  async publish(request: PublishRequest): Promise<PublishResponse> {
    try {
      const response = await apiClient.post<PublishResponse>(
        '/v2/publish/submit',
        request
      );
      return response.data;
    } catch (error: any) {
      console.error('Publish error:', error);
      return {
        success: false,
        error: error.response?.data?.error || { code: 'PUBLISH_FAILED', message: '발행에 실패했습니다.' }
      };
    }
  }

  /**
   * 발행 상태 조회
   */
  async getStatus(jobId: string): Promise<PublishStatusResponse> {
    try {
      const response = await apiClient.get<PublishStatusResponse>(
        `/v2/publish/status?jobId=${encodeURIComponent(jobId)}`
      );
      return response.data;
    } catch (error: any) {
      console.error('Publish status error:', error);
      return {
        success: false,
        error: error.response?.data?.error || { code: 'STATUS_FETCH_FAILED', message: '상태 조회에 실패했습니다.' }
      };
    }
  }

  /**
   * 발행 이력 조회
   */
  async getHistory(filters?: {
    platform?: string;
    status?: string;
    contentId?: number;
    startDate?: string;
    endDate?: string;
    limit?: number;
    offset?: number;
  }): Promise<PublishHistoryResponse> {
    try {
      const params = new URLSearchParams();
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            params.append(key, String(value));
          }
        });
      }

      const response = await apiClient.get<PublishHistoryResponse>(
        `/v2/publish/history?${params.toString()}`
      );
      return response.data;
    } catch (error: any) {
      console.error('Publish history error:', error);
      return {
        success: false,
        error: error.response?.data?.error || { code: 'HISTORY_FETCH_FAILED', message: '이력 조회에 실패했습니다.' }
      };
    }
  }

  /**
   * 토큰 저장
   */
  async saveToken(request: TokenSaveRequest): Promise<TokenSaveResponse> {
    try {
      const response = await apiClient.post<TokenSaveResponse>(
        '/v2/publish/token',
        request
      );
      return response.data;
    } catch (error: any) {
      console.error('Token save error:', error);
      return {
        success: false,
        error: error.response?.data?.error || { code: 'TOKEN_SAVE_FAILED', message: '토큰 저장에 실패했습니다.' }
      };
    }
  }
}

export const publishService = new PublishService();

